(ns arthur.flow.take
  "The shared landmark-to-channel path for synthetic and detected takes."
  (:require [arthur.flow.address :as address]
            [arthur.flow.detect :as detect]
            [arthur.flow.condition :as condition]
            [arthur.flow.condition.brows :as condition-brows]
            [arthur.flow.condition.eyes :as condition-eyes]
            [arthur.flow.condition.interior :as condition-interior]
            [arthur.flow.freeze :as freeze]
            [arthur.flow.measure.anchor :as anchor]
            [arthur.flow.measure.brows :as brows]
            [arthur.flow.measure.eyes :as eyes]
            [arthur.flow.measure.interior :as interior]
            [arthur.flow.measure.mouth :as mouth]
            [arthur.domain.params :as params]))

(def knobs params/defaults)

(defn analysis-for [manifest detector]
  (let [aspect (/ (:width manifest) (:height manifest))]
    (address/analysis
     (cond-> (merge {:detector "mediapipe" :version "unknown"}
                    detector
                    {:source (:source manifest)
                     :footage (:footage manifest)
                     :frames (:frames manifest)
                     :fps (:fps manifest)
                     :aspect aspect
                     ;; How the detector was run, not just which one it was. See
                     ;; `flow/address/analysis-descriptor`.
                     :mode "video" :tracking detect/settings})
       (:range manifest) (assoc :range (:range manifest))))))

(defn measure
  "Condition the anchor before measuring rings through it."
  [{:keys [aspect] :as params} {:keys [dense detected interior presence]}]
  (let [_        (doseq [[id track] presence]
                   (when (not= (count dense) (count track))
                     (throw (ex-info "feature presence track must match the source"
                                     {:feature id :frames (count dense)
                                      :actual (count track)}))))
        fitted   (anchor/fit {:aspect aspect} {:dense dense})
        anchored (condition/anchor params fitted)
        rings    (mouth/measure {:aspect aspect}
                                {:dense dense :transforms (:transforms anchored)})
        eye-data (eyes/measure params {:dense dense :transforms (:transforms anchored)
                                      :detected detected :presence presence})
        brow-data (brows/measure params {:dense dense :transforms (:transforms anchored)})
        inner-data (when interior
                     (condition-interior/apply-defaults
                      params
                      (mapv interior/head-local interior (:transforms anchored)
                            (repeat aspect))
                      (:aperture rings)))]
    (assoc anchored
           :outer (condition/contours params (:outer rings))
           :inner (condition/contours params (:inner rings))
           :aperture (:aperture rings)
           :eyes (condition-eyes/apply-defaults params eye-data)
           :brows (condition-brows/apply-defaults params brow-data eye-data)
           :teeth inner-data
           :detected detected
           :presence presence)))

(defn anchor-base
  "Shared upstream measurement for a feature regeneration."
  [{:keys [aspect] :as params} {:keys [dense detected presence]}]
  (let [fitted (anchor/fit {:aspect aspect} {:dense dense})]
    (assoc (condition/anchor params fitted)
           :detected detected :presence presence)))

(defn measure-part
  "Recompute one feature type through the shared conditioned anchor. A brow
  reads the eye corners, and teeth read mouth aperture; these are input edges,
  not requests to freeze those other features."
  [area {:keys [aspect] :as params}
   {:keys [dense detected interior presence]} {:keys [transforms] :as base}]
  (let [mouth (fn []
                (mouth/measure {:aspect aspect}
                               {:dense dense :transforms transforms}))
        eyes (fn []
               (eyes/measure params {:dense dense :transforms transforms
                                     :detected detected :presence presence}))]
    (merge (select-keys base [:detected :presence])
           (case area
             :mouth (let [m (mouth)]
                      {:outer (condition/contours params (:outer m))
                       :inner (condition/contours params (:inner m))
                       :aperture (:aperture m)})
             :eye {:eyes (condition-eyes/apply-defaults params (eyes))}
             :brow {:brows (condition-brows/apply-defaults
                            params
                            (brows/measure params {:dense dense :transforms transforms})
                            (eyes))}
             :teeth (let [aperture (:aperture (mouth))]
                      {:teeth (condition-interior/apply-defaults
                               params
                               (mapv interior/head-local interior transforms
                                     (repeat aspect))
                               aperture)})
             (throw (ex-info "unknown measured feature type" {:area area}))))))

(defn subjects
  "Per-subject source landmarks -> per-subject conditioned measurements.

  Every tracked face in one shot goes through the same measurement path and is
  conditioned on its OWN anchor fit, because the fit maps a face onto its own
  mean pose: sharing one would register the second face against the first one's
  head. What they share is the video, and that is already expressed by them
  sharing the analysis record."
  [params by-subject]
  (into {} (map (fn [[id inputs]] [id (measure params inputs)])) by-subject))

(defn build
  "`by-subject` is subject id -> that face's source tracks, one entry per
  tracked face."
  [params by-subject]
  (freeze/clip params (subjects params by-subject)))

(defn footage
  "A real manifest and its detected landmarks through the same measurement and
  freeze path as the synthetic take. Symbols keep the source fps; the reader
  selects native frames on the project grid."
  [manifest {:keys [dimensions detector subjects]}]
  (let [[w h] dimensions
        params (merge knobs
                      {:name (or (:source manifest) "footage")
                       :fps (:fps manifest) :aspect (/ w h)
                       :stage [320 200] :fit-motion? true
                       :expose 1
                       ;; The detector's identity comes from the server, which
                       ;; hashes the model asset it serves rather than trusting a
                       ;; version string somebody has to remember to bump. See
                       ;; `flow/address`: a model upgrade that silently reused
                       ;; these landmarks is the failure this prevents.
                       :analysis (analysis-for (assoc manifest :width w :height h)
                                               detector)
                       ;; What the faces are traced over: these frames of this
                       ;; footage, at the pixel size its stills are. See
                       ;; `freeze/footage-symbol`.
                       :footage {:id (:id manifest)
                                 :range (or (:range manifest) [0 (:frames manifest)])
                                 :width (or (:width manifest) w)
                                 :height (or (:height manifest) h)}})]
    (build params subjects)))
